import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'
import { cn } from '@/lib/utils'

const button = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-[background-color,color,box-shadow,opacity] duration-150 disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg shadow-sm hover:brightness-110 active:brightness-95',
        outline: 'border border-line-strong bg-surface text-ink hover:bg-surface-2',
        ghost: 'text-ink-muted hover:bg-surface-2 hover:text-ink',
        // Leaving a call and ending one for everyone are the two actions that
        // cannot be walked back. They get the only red in the component set.
        danger: 'bg-critical text-white hover:brightness-110',
        subtle: 'bg-accent-soft text-accent hover:brightness-105 dark:hover:brightness-125',
      },
      size: {
        sm: 'h-8 px-3 text-[0.8125rem] pointer-coarse:min-h-11 [&_svg]:size-3.5',
        md: 'h-9 px-4 text-sm pointer-coarse:min-h-11 [&_svg]:size-4',
        lg: 'h-11 px-6 text-[0.9375rem] [&_svg]:size-[1.125rem]',
        icon: 'h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 [&_svg]:size-4',
        // Join / start-a-call. A primary action that is the reason the page
        // exists should be reachable without aiming.
        hero: 'h-12 px-7 text-base [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'outline', size: 'md' },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button> & { asChild?: boolean }

export function Button({ className, variant, size, asChild, ...props }: ButtonProps) {
  const Component = asChild ? Slot : 'button'
  return <Component className={cn(button({ variant, size }), className)} {...props} />
}

export { button as buttonVariants }
